import React, { useState, useRef, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { FiPaperclip, FiX } from 'react-icons/fi';
import { uploadBookCommunityImageToTmpFile, createBookcommunityDefaultBoardText, getBookcommunityDefaultBoardText, modifyBookcommunityDefaultBoardText } from '../api/BookcommunityApi';
import { BookcommunityBoardTextInputReqDTO, BookcommunityBoardTextFileDTO } from '../dto/BookcommunityDTOs';
import { useNoobookContext } from '../context/NoobookContext';
import { getAccessToken } from '../react-queries/queries';
import { processApiResponse } from '../common/errorMessage';
import MessageModal from '../components/MessageModal';
import LoadingOverlay from '../components/LoadingOverlay';

// 모달 오버레이
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

// 모달 컨테이너
const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 85%;
  max-width: 400px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

// 모달 헤더
const ModalHeader = styled.div`
  padding: 20px 20px 16px 20px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  text-align: center;
`;

// 모달 바디
const ModalBody = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
`;

// 텍스트 입력 영역
const TextInputContainer = styled.div`
  position: relative;
  flex: 1;
  min-height: 150px;
`;

const TextArea = styled.textarea`
  width: calc(100% - 24px);
  height: 150px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #007bff;
  }

  &::placeholder {
    color: #999;
  }
`;

// 첨부 버튼
const AttachButton = styled.button`
  position: absolute;
  bottom: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background-color: #007bff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.3);

  &:hover {
    background-color: #0056b3;
  }

  &:active {
    transform: scale(0.95);
  }
`;

// 파일 리스트 컨테이너
const FileListContainer = styled.div`
  margin-top: 8px;
`;

const FileListTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #666;
  margin-bottom: 8px;
`;

const FileList = styled.div`
  display: flex;
  gap: 12px;
  overflow-x: auto;
  padding: 8px 0;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }
`;

const FileItem = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
`;

const FileThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FileVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 12px;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }
`;



// 숨겨진 파일 입력
const HiddenFileInput = styled.input`
  display: none;
`;


const BoardEditBtnRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  padding: 20px;
  border-top: 1px solid #f0f0f0;
`;

const BoardEditBtn = styled.button`
  background: #eee;
  color: #555;
  border: none;
  border-radius: 8px;
  padding: 6px 18px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  min-width: 64px;
`;

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'webm', 'm4v', 'avi', 'mkv', '3gp', 'quicktime'];

const getFileExt = (file) => {
  if (file?.name?.includes('.')) {
    return file.name.split('.').pop().toLowerCase();
  }
  if (file?.type?.includes('/')) {
    return file.type.split('/')[1].toLowerCase();
  }
  return '';
};

const isVideoFile = (fileItem) => {
  if (fileItem.file?.type?.startsWith('video/')) {
    return true;
  }
  const ext = (fileItem.fileExt || '').toLowerCase().replace(/^\./, '');
  if (VIDEO_EXTENSIONS.includes(ext)) {
    return true;
  }
  const name = (fileItem.fileName || '').toLowerCase();
  return VIDEO_EXTENSIONS.some(e => name.endsWith('.' + e));
};




export default function MoimDetailCommunityFreeboardEditPage({ bookCommunityId, bookCommunityBoardTextId, onClose }) {
  const [text, setText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);
  const selectedFilesRef = useRef([]);
  const [errorMessage, setErrorMessage] = useState('');
  selectedFilesRef.current = selectedFiles;

  useEffect(() => {
    loadBoardText();
  }, [bookCommunityBoardTextId]);

  useEffect(() => {
    return () => {
      selectedFilesRef.current.forEach((fileItem) => {
        if (fileItem.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(fileItem.preview);
        }
      });
    };
  }, []);


  const loadBoardText = async () => {
    if(!bookCommunityBoardTextId) {
      return;
    }
    if(isLoading) {
      return;
    }
    setIsLoading(true);
    try {
      const { accessToken } = await getAccessToken();
      if (!accessToken) {
        return;
      }
      const response = await getBookcommunityDefaultBoardText(accessToken || '', bookCommunityBoardTextId);
      const processedResponse = processApiResponse(response);
      if(processedResponse.success) {
        setText(processedResponse.data.boardText);
        if(processedResponse.data.files) {
          setSelectedFiles(processedResponse.data.files.map(file => ({
            file:null,
            preview: file.fileUrl,
            id: Date.now() + Math.random(),
            textFileId: file.id,
            fileName: file.fileName,
            fileExt: file.fileExt,
            fileStoreId: file.fileStoreId,
            fileNo: file.fileNo
          })));
        }
      }
    } catch (error) {
      console.error('게시글 데이터 로드 중 오류:', error);
      setErrorMessage('게시글 데이터를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 파일 선택 핸들러
  const handleFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files);
    if((selectedFiles.length + files.length) > 8) {
      setErrorMessage('첨부 파일은 최대 8개까지 가능합니다.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    files.forEach(file => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      if (!isImage && !isVideo) {
        setErrorMessage('이미지 또는 동영상 파일만 첨부할 수 있습니다.');
        return;
      }
      const preview = isVideo ? URL.createObjectURL(file) : null;
      const addFile = (previewUrl) => {
        setSelectedFiles(prev => [...prev, {
          file,
          preview: previewUrl,
          id: Date.now() + Math.random(),
          textFileId: null,
          fileName: file.name,
          fileExt: getFileExt(file),
          fileStoreId: null,
          fileNo: null
        }]);
      };
      if (isVideo) {
        addFile(preview);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        addFile(e.target.result);
      };
      reader.readAsDataURL(file);
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedFiles]);

  // 첨부 버튼 클릭 핸들러
  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  // 파일 제거 핸들러
  const handleRemoveFile = (fileId) => {
    setSelectedFiles(prev => {
      const target = prev.find(file => file.id === fileId);
      if (target?.preview?.startsWith('blob:')) {
        URL.revokeObjectURL(target.preview);
      }
      return prev.filter(file => file.id !== fileId);
    });
  };

  // 취소 핸들러
  const handleCancel = () => {
    onClose(false);
  };

  // 저장 핸들러
  const handleSave = async () => {
    if (!text.trim()) {
      setErrorMessage('내용을 입력해주세요.');
      return;
    }

    if(isLoading) {
      return;
    }
    

    setIsLoading(true);
    try {
      const { accessToken } = await getAccessToken();
      if (!accessToken) {
        return;
      }
      // 파일들을 임시 파일로 업로드
      const fileDTOs = [];
      for (const fileItem of selectedFiles) {
        if(fileItem.textFileId) {
          const fileDTO = new BookcommunityBoardTextFileDTO();
          fileDTO.id = fileItem.textFileId;
          fileDTO.fileStoreId = fileItem.fileStoreId;
          fileDTO.fileName = fileItem.fileName;
          fileDTO.fileExt = fileItem.fileExt;
          fileDTO.fileNo = fileItem.fileNo;
          fileDTOs.push(fileDTO);
          continue;
        }
        const response = await uploadBookCommunityImageToTmpFile(accessToken, fileItem.file);
        const processedResponse = processApiResponse(response);
        if (processedResponse.success && processedResponse.data) {
          const fileDTO = new BookcommunityBoardTextFileDTO();
          fileDTO.id = null;
          fileDTO.fileStoreId = processedResponse.data.fileId;
          fileDTO.fileName = processedResponse.data.fileNm;
          fileDTO.fileExt = processedResponse.data.ext;
          fileDTO.fileUrl = processedResponse.data.fileUrl;
          fileDTOs.push(fileDTO);
        }
      }

      // 게시글 생성 요청 DTO
      const requestDTO = new BookcommunityBoardTextInputReqDTO();
      requestDTO.boardText = text.trim();
      requestDTO.files = fileDTOs;

      // API 호출
      const response = bookCommunityBoardTextId 
         ? await modifyBookcommunityDefaultBoardText(accessToken, bookCommunityBoardTextId, requestDTO) 
         : await createBookcommunityDefaultBoardText(accessToken, bookCommunityId, requestDTO);
      const processedResponse = processApiResponse(response);
      if (processedResponse.success) {
        onClose(true);
      } else {
        setErrorMessage(processedResponse.errorMessage || '저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('저장 중 오류:', error);
      setErrorMessage(error.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };


  const handleCloseErrorModal = () => {
    setErrorMessage('');
  };

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && handleCancel()}>
      <ModalContainer>
        <ModalHeader>
          <ModalTitle>{bookCommunityBoardTextId ? '글 수정' : '글쓰기'}</ModalTitle>
        </ModalHeader>
        
        <ModalBody>
          <TextInputContainer>
            <TextArea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="메모를 입력하세요..."
            />
            <AttachButton onClick={handleAttachClick}>
              <FiPaperclip size={18} />
            </AttachButton>
          </TextInputContainer>

          {selectedFiles.length > 0 && (
            <FileListContainer>
              <FileListTitle>첨부 파일</FileListTitle>
              <FileList>
                {selectedFiles.map((fileItem) => (
                  <FileItem key={fileItem.id}>
                    {isVideoFile(fileItem) ? (
                      <FileVideo src={fileItem.preview} muted playsInline preload="metadata" />
                    ) : (
                      <FileThumbnail src={fileItem.preview} alt="파일 미리보기" />
                    )}
                    <RemoveButton onClick={() => handleRemoveFile(fileItem.id)}>
                      <FiX size={12} />
                    </RemoveButton>
                  </FileItem>
                ))}
              </FileList>
            </FileListContainer>
          )}
        </ModalBody>       
          <BoardEditBtnRow>
          <BoardEditBtn onClick={handleCancel} disabled={isLoading}>
            취소
          </BoardEditBtn>
          <BoardEditBtn style={{background:'#244ac2', color:'#fff'}} onClick={handleSave} disabled={isLoading}>
            {isLoading ? '저장 중...' : '저장'}
          </BoardEditBtn>
          </BoardEditBtnRow>
        <HiddenFileInput
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileSelect}
        />
      </ModalContainer>

      {/* 에러 메시지 모달 */}
      <MessageModal
        isOpen={!!errorMessage}
        message={errorMessage}
        onClose={handleCloseErrorModal}
        title="오류"
      />
      
      {/* 로딩 오버레이 */}
      {isLoading && <LoadingOverlay />}

    </ModalOverlay>
  );
}